import { Alert, Button, Dialog, DialogActions, DialogContent, Stack, Typography } from '@customink/pigment-react';
import { useCallback, useState } from 'react';
import { useCardRemove, useCards } from '~/adapters/braintree/hooks';
import { Loader } from '~/components/Loader/loader';
import { useNotifications } from '~/contexts/NotificationContext';
import { CardRow } from '../../components/CardRow';
import { EmptyCardList } from './EmptyCardList';
import { sendCardResultEvent, sendRemoveCardModalEvent, sendVaultedPaymentsEvent } from './sendTrackingEvents';

interface RemovingCard {
    ending: string;
    nonce: string;
    cardType: string;
}

export function PaymentCardList() {
    const { isLoading, data: cards } = useCards();
    const [cardToBeRemoved, setCardToBeRemoved] = useState<RemovingCard | null>(null);
    const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
    const { mutateAsync } = useCardRemove();
    const { pushSuccessNotification } = useNotifications();

    const openRemoveModal = useCallback(
        (card: RemovingCard) => {
            setCardToBeRemoved(card);
            setIsRemoveModalVisible(true);
            sendVaultedPaymentsEvent('Remove');
        },
        [setIsRemoveModalVisible, setCardToBeRemoved],
    );
    const closeRemoveModal = useCallback(() => {
        setCardToBeRemoved(null);
        setIsRemoveModalVisible(false);
        sendRemoveCardModalEvent('Cancel');
    }, [setIsRemoveModalVisible, setCardToBeRemoved]);

    const removeCard = useCallback(async () => {
        sendRemoveCardModalEvent('Remove');
        if (!cardToBeRemoved) {
            throw new Error("No card is selected. Can't remove.");
        }
        try {
            await mutateAsync({ singleUseTokenId: cardToBeRemoved.nonce });
            pushSuccessNotification(`${cardToBeRemoved.cardType} **${cardToBeRemoved.ending} has been removed.`);
            sendCardResultEvent('Remove Card Success');
            setCardToBeRemoved(null);
            closeRemoveModal();
        } catch (e) {
            sendCardResultEvent('Remove Card Failed');
        }
    }, [mutateAsync, cardToBeRemoved, setCardToBeRemoved, closeRemoveModal]);

    if (isLoading || !cards) {
        return <Loader title="Loading..." devLabel="braintree API" />;
    }

    if (cards.length === 0) {
        return <EmptyCardList />;
    }

    return (
        <>
            <Stack gap={2}>
                {cards.map((card) => (
                    <CardRow
                        key={card.nonce}
                        cardType={card.details.cardType}
                        lastFour={card.details.lastFour}
                        onRemove={() =>
                            openRemoveModal({
                                nonce: card.nonce,
                                ending: card.details.lastFour,
                                cardType: card.details.cardType,
                            })
                        }
                    />
                ))}
            </Stack>
            <Dialog title="Remove Payment Method" open={isRemoveModalVisible} onClose={closeRemoveModal}>
                <DialogContent>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        <Typography variant="bodyLong1">
                            The {cardToBeRemoved?.cardType} ending in **{cardToBeRemoved?.ending} will be removed from
                            your Payment Methods.
                        </Typography>
                    </Alert>
                    <Typography variant="bodyLong1">
                        You will no longer be able to select this payment method during checkout. You can still use this
                        card by entering your details manually or re-adding it to your Payment Methods.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={removeCard}>Remove</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
